import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { exitInstitute, setInstituteSwitching } from "../../store/slices/instituteContextSlice";
import { setUser } from "../../store/slices/authSlice";
import { usePermissions } from "../../hooks/usePermissions";
import instituteService from "../../services/instituteService";
import profileService from "../../services/profileService";
import { useQueryClient } from "@tanstack/react-query";
import { applyWebSessionTokens } from "../../utils/sessionRevocation";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  Avatar,
  Divider,
  IconButton,
  Skeleton,
} from "@mui/material";
import {
  Dashboard,
  Person,
  Assignment,
  MenuBook,
  CalendarMonth,
  EventNote,
  Event,
  Assessment,
  Chat,
  Notifications,
  People,
  School,
  Business,
  Settings,
  AdminPanelSettings,
  SupervisorAccount,
  CloudUpload,
  Security,
  ExpandLess,
  ExpandMore,
  Class,
  Subject,
  Grade,
  AccessTime,
  EventAvailable,
  Groups,
  Campaign,
  Forum,
  ExitToApp,
  Schedule,
  AutoAwesome,
  HelpOutline,
  Support,
  Payments,
  ListAlt,
  AccountBalance,
  Tune,
  Description,
} from "@mui/icons-material";
import { getAvatarInitial } from "../../utils/avatarUtils";
import { shouldUsePlatformBranding, getPlatformBranding } from "../../utils/branding";
import { MENU_CONFIG } from "../../config/menuConfig";
import { LEAVE_REVIEW_MENU_PERMISSIONS } from "../../utils/leavePermissions";
import { EXAM_MODULE_PERMISSIONS } from "../../utils/examPermissions";
import { CHAT_MENU_PERMISSIONS } from "../../utils/chatPermissions";
import { EDIT_LECTURE_MENU_PERMISSIONS } from "../../utils/editLecturePermissions";

function shouldDebugSidebar() {
  try {
    if (typeof window === "undefined") return false;
    if (process.env.NODE_ENV === "development") return true;
    const qs = new URLSearchParams(window.location.search || "");
    return (
      window?.localStorage?.getItem("debugSidebar") === "1" ||
      window?.sessionStorage?.getItem("debugSidebar") === "1" ||
      qs.get("debugSidebar") === "1" ||
      qs.get("debug") === "1"
    );
  } catch {
    return false;
  }
}

function debugSidebar(...args) {
  try {
    if (!shouldDebugSidebar()) return;
    // eslint-disable-next-line no-console
    console.log("[sidebar]", ...args);
  } catch {
    // ignore
  }
}

// Icon name -> component map for dynamic menu
const MENU_ICONS = {
  Dashboard, Person, Assignment, MenuBook, CalendarMonth, EventNote, Assessment,
  Chat, Notifications, People, School, Business, Settings, AdminPanelSettings,
  SupervisorAccount, CloudUpload, Security, Class, Subject, Grade, AccessTime,
  EventAvailable, Campaign, AutoAwesome, HelpOutline, Support, Groups, Description
};

const Sidebar = ({ user: userProp, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [openMenus, setOpenMenus] = React.useState({});
  const navScrollRef = React.useRef(null);
  const menuRefs = React.useRef({});

  // Always use Redux state to get the latest user, not the prop
  const { user: reduxUser } = useSelector((state) => state.auth);
  const user = reduxUser || userProp; // Fallback to prop if Redux not available

  const rawRole = user?.role?.slug || user?.role || "student";
  const normalizedRole = typeof rawRole === 'string' ? rawRole.toLowerCase() : rawRole;
  const userRole = normalizedRole === "institute" ? "admin" : normalizedRole;
  const { hasPermission, hasVisiblePermission, hasAnyPermission, hasAllPermissions, isModuleEnabled } =
    usePermissions();

  // Get institute context for super admin
  const { selectedInstitute, isInInstituteContext } = useSelector(
    (state) => state.instituteContext,
  );

  // Base role path for the current user (must match App.js rolePath).
  const rolePath = `/${userRole}`;

  const usePlatformBranding = shouldUsePlatformBranding({
    userRole,
    isInInstituteContext,
    pathname: location.pathname,
  });
  const platformBranding = getPlatformBranding();

  // When Super Admin switches into an institute, institute-scoped pages live under /admin/*.
  // Use that as the effective base for menus/routes in that context.
  const effectiveRolePath =
    userRole === "superadmin" && isInInstituteContext ? "/admin" : rolePath;

  // For menu visibility rules, treat superadmin-in-institute-context like admin.
  const menuRole = userRole === "superadmin" && isInInstituteContext ? "admin" : userRole;

  React.useEffect(() => {
    debugSidebar("context", {
      rawRole,
      normalizedRole,
      userRole,
      menuRole,
      isInInstituteContext,
      selectedInstituteId: selectedInstitute?.id ?? selectedInstitute?._id ?? null,
      rolePath,
      effectiveRolePath,
    });
    debugSidebar("permissions", {
      role_manage: hasPermission("role.manage"),
      permission_manage: hasPermission("permission.manage"),
      rbac_manage: hasPermission("rbac.manage"),
      audit_read: hasPermission("audit.read"),
      role_assign: hasPermission("role.assign"),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawRole, normalizedRole, userRole, isInInstituteContext, selectedInstitute, rolePath, effectiveRolePath]);

  const handleExitInstitute = async () => {
    console.log('handleExitInstitute - starting');
    dispatch(
      setInstituteSwitching({
        active: true,
        message: "Returning to Super Admin panel...",
      })
    );
    const originalSuperAdminId = localStorage.getItem("originalSuperAdminId");
    try {
    if (originalSuperAdminId) {
      // Try switching back to super admin, but always perform cleanup regardless of API result
      try {
        console.log('handleExitInstitute - calling API');
        const response = await instituteService.switchBackToSuperAdmin(originalSuperAdminId);
        const { token, refreshToken } = response.data.data || {};
        if (token) {
          // New session for the same user: store tokens and notify the socket
          // (myams:session-changed) so it re-handshakes into the new session room.
          applyWebSessionTokens({ accessToken: token, refreshToken: refreshToken || null });

          // Decode the new token to update user in localStorage
          try {
            const tokenParts = token.split('.');
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));
              const rawRole = payload.role || payload.userRole || payload.roleName;
              console.log('handleExitInstitute - new rawRole from token:', rawRole);
              const r = rawRole ? String(rawRole).toLowerCase().trim() : "";
              const normalizedRole = r === "super admin" || r === "superadmin" ? "superadmin" : (r ? r.replace(/\s+/g, "") : rawRole);

              const newUser = {
                ...payload,
                id: payload.id || payload.userId || payload.sub,
                email: payload.email,
                role: normalizedRole,
                name: payload.name || payload.username || payload.fullName,
                firstName: payload.firstName || payload.first_name,
                lastName: payload.lastName || payload.last_name,
                consentFlag: payload.consentFlag ?? payload.consent_flag ?? 1,
                acceptedPrivacyPolicyId:
                  payload.acceptedPrivacyPolicyId ||
                  payload.accepted_privacy_policy_id ||
                  (() => {
                    try {
                      return JSON.parse(localStorage.getItem("user") || "{}")?.acceptedPrivacyPolicyId || null;
                    } catch {
                      return null;
                    }
                  })(),
              };
              delete newUser.operatingInstituteId;
              delete newUser.instituteId;
              delete newUser.isManagingInstitute;

              localStorage.setItem("user", JSON.stringify(newUser));
              // Update Redux user state with token-derived user
              dispatch(setUser(newUser));

              // Fetch superadmin profile so sidebar/header show superadmin photo immediately
              try {
                const detailsRes = await profileService.getSuperAdminDetails();
                const raw =
                  detailsRes?.data?.data ??
                  detailsRes?.data?.user ??
                  detailsRes?.data ??
                  detailsRes;
                const profileImage = raw?.profileImage ?? raw?.profile_image;
                if (raw && typeof raw === "object" && profileImage) {
                  const userWithPhoto = { ...newUser, profileImage };
                  dispatch(setUser(userWithPhoto));
                  localStorage.setItem("user", JSON.stringify(userWithPhoto));
                }
              } catch (e) {
                // Non-blocking: superadmin photo will load when user visits Profile
              }
            }
          } catch (e) {
            console.warn('handleExitInstitute - failed decoding token payload', e);
          }
        }
      } catch (error) {
        console.error("Failed to switch back to super admin API:", error);
      } finally {
        // Always clear institute context and originalSuperAdminId even if API fails
        console.log('handleExitInstitute - performing final cleanup');
        dispatch(exitInstitute());
        localStorage.removeItem('originalSuperAdminId');
        localStorage.removeItem('selectedInstitute');
        localStorage.removeItem('isInInstituteContext');

        // Ensure app state is in sync by invoking checkAuth
        try {
          const { checkAuth } = await import('../../store/slices/authSlice');
          dispatch(checkAuth());
        } catch (e) {
          console.warn('handleExitInstitute - checkAuth dispatch failed', e);
        }

        // Clear query cache
        try { queryClient.clear(); } catch (e) { /* ignore */ }

        // Navigate to superadmin institutes route
        navigate('/superadmin/institutes');
      }
    } else {
      // Just exit context without API call
      console.log('handleExitInstitute - no originalSuperAdminId, just clearing context');
      dispatch(exitInstitute());
      localStorage.removeItem('selectedInstitute');
      localStorage.removeItem('isInInstituteContext');
      localStorage.removeItem('originalSuperAdminId');
      try {
        const { checkAuth } = await import('../../store/slices/authSlice');
        dispatch(checkAuth());
      } catch (e) {
        console.warn('handleExitInstitute - checkAuth dispatch failed', e);
      }
      queryClient.clear();
      navigate('/superadmin/institutes');
    }
    } finally {
      dispatch(setInstituteSwitching({ active: false }));
    }
  };

  const ensureMenuVisible = React.useCallback((menuTitle) => {
    const container = navScrollRef.current;
    const menuNode = menuRefs.current?.[menuTitle];
    if (!container || !menuNode) return;

    const containerRect = container.getBoundingClientRect();
    const menuRect = menuNode.getBoundingClientRect();
    const visibilityPadding = 90;

    // Keep full opened submenu block visible inside sidebar viewport
    if (menuRect.top < containerRect.top + visibilityPadding) {
      container.scrollBy({
        top: menuRect.top - containerRect.top - visibilityPadding,
        behavior: "smooth",
      });
      return;
    }
    if (menuRect.bottom > containerRect.bottom - visibilityPadding) {
      container.scrollBy({
        top: menuRect.bottom - containerRect.bottom + visibilityPadding,
        behavior: "smooth",
      });
    }
  }, []);

  const handleMenuToggle = (menu) => {
    setOpenMenus((prev) => {
      const willOpen = !prev[menu];
      // Wait for Collapse animation to render submenu height, then scroll if needed
      if (willOpen) {
        setTimeout(() => ensureMenuVisible(menu), 220);
      }
      return { ...prev, [menu]: willOpen };
    });
  };

  const handleNavigation = (path) => {
    debugSidebar("navigate requested", { path });
    if (!path || typeof path !== "string" || !path.trim()) {
      debugSidebar("navigate skipped (empty path)", { path });
      return;
    }
    navigate(path);
    if (onClose) onClose();
  };

  const isActive = (path) => location.pathname === path;
  const isMenuActive = (paths) =>
    paths.some((path) => location.pathname.startsWith(path));

  const menuItemStyles = (active) => ({
    borderRadius: 2,
    mb: 0.5,
    mx: 1,
    bgcolor: active ? "rgba(255,255,255,0.1)" : "transparent",
    "&:hover": {
      bgcolor: "rgba(255,255,255,0.08)",
    },
    color: active ? "#fff" : "rgba(255,255,255,0.7)",
  });

  const filterByPermission = (items) => {
    if (!items || !Array.isArray(items)) return [];
    return items
      .map((item) => {
        if (item?.id === "m9t7gv") {
          debugSidebar("evaluate RBAC menu", {
            item,
            userRole,
            normalizedRole,
            hasRbacManage: hasPermission("rbac.manage"),
            hasRoleAssign: hasPermission("role.assign"),
            hasAuditRead: hasPermission("audit.read"),
          });
        }
        if (item.moduleKey && !isModuleEnabled(item.moduleKey)) return null;
        if (item.permission && !hasVisiblePermission(item.permission)) return null;
        if (item.permissions && !item.permissions.some((p) => hasVisiblePermission(p))) return null;
        if (item.permissionsAll && !hasAllPermissions(item.permissionsAll)) return null;
        if (item.submenu) {
          const filteredSub = filterByPermission(item.submenu);
          if (filteredSub.length === 0) return null;
          return { ...item, submenu: filteredSub };
        }
        return item;
      })
      .filter(Boolean);
  };

  const itemPassesPermission = (item) => {
    if (item.id === "support-inbox") {
      return String(userRole || "").toLowerCase() === "superadmin";
    }
    if (item.moduleKey && !isModuleEnabled(item.moduleKey)) return false;

    // Super Admin panel must remain clean and role-scoped.
    // Even if superadmin has every permission, menuConfig.excludeRoles must still hide non-superadmin menus.
    if (
      (menuRole === "superadmin" || normalizedRole === "superadmin") &&
      item.excludeRoles &&
      (item.excludeRoles.includes("superadmin") ||
        item.excludeRoles.includes(menuRole) ||
        item.excludeRoles.includes(normalizedRole))
    ) {
      return false;
    }

    const hasExplicitPermission =
      (item.permission && hasVisiblePermission(item.permission)) ||
      (item.permissions && item.permissions.some((p) => hasVisiblePermission(p))) ||
      (item.permissionsAll && hasAllPermissions(item.permissionsAll)) ||
      (item.requirePermission && hasVisiblePermission(item.requirePermission)) ||
      (item.requireAny && hasAnyPermission(item.requireAny));

    // Permission matrix wins: custom roles / teachers with admin grants are not blocked by excludeRoles.
    if (hasExplicitPermission) {
      if (item.onlyRoles && !item.onlyRoles.includes(menuRole) && !item.onlyRoles.includes(normalizedRole)) {
        return false;
      }
      return true;
    }

    if (item.excludeRoles && (item.excludeRoles.includes(menuRole) || item.excludeRoles.includes(normalizedRole))) {
      return false;
    }
    if (item.onlyRoles && !item.onlyRoles.includes(menuRole) && !item.onlyRoles.includes(normalizedRole)) {
      return false;
    }
    if (item.permission) return false;
    if (item.permissions) return false;
    if (item.permissionsAll) return false;
    if (item.requirePermission) return false;
    if (item.requireAny) return false;
    return true;
  };

  const buildMenuFromConfig = () => {
    const resolveItem = (item) => {
      let path = item.path || "";
      if (path.includes("{rolePath}")) path = path.replace(/\{rolePath\}/g, effectiveRolePath);
      const IconComp = MENU_ICONS[item.icon] || Dashboard;
      const out = {
        ...item,
        title: item.title,
        icon: React.createElement(IconComp),
        path,
      };
      if (Array.isArray(item.submenu)) {
        // Recursively resolve submenu entries (icons/paths) and apply permission filtering.
        const subResolved = item.submenu
          .filter(itemPassesPermission)
          .map(resolveItem);
        out.submenu = subResolved;
      }
      return out;
    };

    let resolved = MENU_CONFIG.filter(itemPassesPermission).map(resolveItem);

    // Pure Super Admin panel should not show institute Administration menus.
    // Those are only relevant when superadmin is acting inside an institute context (/admin/*).
    if (userRole === "superadmin" && !isInInstituteContext) {
      resolved = resolved.filter((item) => item?.group !== "Administration");
    }

    const groupOrder = [
      "Student - Academics",
      "Student - Attendance & Leaves",
      "Administration",
      "Academics",
      "Department",
      "Attendance",
      "Schedule & Timetable",
      "Leave and Request",
      "Account and Support",
      "Super Admin",
    ];
    const groupIcons = {
      "Student - Academics": School,
      "Student - Attendance & Leaves": EventNote,
      Administration: AdminPanelSettings,
      Academics: School,
      Department: Business,
      Attendance: EventAvailable,
      "Schedule & Timetable": Schedule,
      "Leave and Request": EventNote,
      "Account and Support": Support,
      "Super Admin": SupervisorAccount,
    };

    const topLevel = [];
    const byGroup = {};

    resolved.forEach((item) => {
      if (item.group) {
        if (!byGroup[item.group]) byGroup[item.group] = [];
        byGroup[item.group].push(item);
      } else {
        topLevel.push(item);
      }
    });

    groupOrder.forEach((group) => {
      if (group === "Super Admin" && userRole !== "superadmin") return;
      const items = byGroup[group];
      if (items && items.length > 0) {
        const IconComp = groupIcons[group] || Business;
        topLevel.push({
          title: group,
          icon: React.createElement(IconComp),
          submenu: items,
        });
      }
    });

    Object.keys(byGroup)
      .filter((g) => !groupOrder.includes(g))
      .filter((g) => g !== "Super Admin" || userRole === "superadmin")
      .forEach((group) => {
        topLevel.push({
          title: group,
          icon: React.createElement(Business),
          submenu: byGroup[group],
        });
      });

    return filterByPermission(topLevel);
  };

  // Super Admin institute context: mirror the same group structure as RBAC-driven menus
  const instituteContextMenus = [
    { title: "Dashboard", icon: <Dashboard />, path: "/dashboard" },
    {
      title: "Administration",
      icon: <AdminPanelSettings />,
      submenu: [
        { title: "Users", icon: <People />, path: `${effectiveRolePath}/users`, permission: "user.read" },
        { title: "Departments", icon: <Business />, path: `${effectiveRolePath}/departments`, permission: "department.read" },
        {
          title: "RBAC",
          icon: <Security />,
          permissions: ["rbac.manage", "role.assign"],
          submenu: [
            { title: "Roles & Modules", icon: <Security />, path: `${effectiveRolePath}/rbac`, permissions: ["rbac.manage", "role.assign"] },
            { title: "Role Permissions", icon: <Security />, path: `${effectiveRolePath}/roles-permissions`, permissions: ["rbac.manage", "role.assign"] },
          ],
        },
        {
          title: "Leave Management",
          icon: <EventNote />,
          path: `${effectiveRolePath}/leave-management`,
          permissions: ["leave.read", "leave.update", "leave.create"],
        },
        {
          title: "Import/Export",
          icon: <CloudUpload />,
          path: `${effectiveRolePath}/import`,
          permissions: [
            "user.import",
            "department.import",
            "course.import",
            "class.import",
            "subject.import",
            "student.profile.create",
          ],
        },
        { title: "Audit Logs", icon: <Assessment />, path: `${effectiveRolePath}/audit-logs`, permission: "audit.read" },
        { title: "Global Notifications", icon: <Notifications />, path: "/superadmin/global-notifications" },
        { title: "Broadcast & Groups", icon: <Groups />, path: "/superadmin/broadcast" },
      ],
    },
    {
      title: "Academics",
      icon: <School />,
      submenu: [
        { title: "Assignments", icon: <Assignment />, path: `${effectiveRolePath}/assignments`, permission: "assignment.read" },
        {
          title: "Grade Submissions",
          icon: <Assignment />,
          path: `${effectiveRolePath}/assignment-approval`,
          permissions: ["assignment.approve", "assignment.reject"],
        },
        { title: "Materials", icon: <MenuBook />, path: `${effectiveRolePath}/materials`, permission: "material.read" },
        { title: "Exams", icon: <Grade />, path: `${effectiveRolePath}/exams`, permissions: EXAM_MODULE_PERMISSIONS },
        {
          title: "Exam Management",
          icon: <Grade />,
          path: `${effectiveRolePath}/exam-management`,
          permissions: EXAM_MODULE_PERMISSIONS,
        },
        { title: "Academic Info", icon: <School />, path: "/academic-info", permission: "academic.read" },
        { title: "Reports & Analytics", icon: <Assessment />, path: `${effectiveRolePath}/reports`, permission: "report.read" },
      ],
    },
    {
      title: "Department",
      icon: <Business />,
      submenu: [
        { title: "Student", icon: <People />, path: `${effectiveRolePath}/students`, permission: "student.profile.read" },
        { title: "Teachers", icon: <People />, path: `${effectiveRolePath}/teachers`, permission: "teacher.profile.read" },
        { title: "Courses", icon: <Class />, path: `${effectiveRolePath}/courses`, permission: "course.read" },
        { title: "Classes", icon: <Class />, path: `${effectiveRolePath}/classes`, permission: "class.read" },
        { title: "Subjects", icon: <Subject />, path: `${effectiveRolePath}/subjects`, permission: "subject.read" },
        { title: "Department Info", icon: <Business />, path: "/department", permissions: ["department.info", "department.read"] },
        { title: "Documents", icon: <Description />, path: `${effectiveRolePath}/documents`, permission: "documents.view" },
        { title: "Class Notifications", icon: <Campaign />, path: `${effectiveRolePath}/class-notifications`, permission: "notification.create_class" },
      ],
    },
    {
      title: "Attendance",
      icon: <EventAvailable />,
      submenu: [
        // Menu visibility: allow attendance.read to show module entry; action APIs still enforce start/mark permissions.
        { title: "Smart Attendance", icon: <EventAvailable />, path: `${effectiveRolePath}/smart-attendance`, permissions: ["attendance.read", "attendance.start", "attendance.mark", "attendance.mark_manual"] },
        { title: "Manual Attendance Upload", icon: <CloudUpload />, path: `${effectiveRolePath}/attendance/manual-upload`, permissions: ["attendance.read", "attendance.mark_manual", "attendance.start"] },
        { title: "Special Sessions", icon: <EventNote />, path: `${effectiveRolePath}/special-sessions`, permissions: ["special_session.read", "special_session.manage", "attendance.read", "attendance.start", "attendance.mark_manual"] },
      ],
    },
    {
      title: "Schedule & Timetable",
      icon: <Schedule />,
      submenu: [
        { title: "Timetable Dashboard", icon: <CalendarMonth />, path: `${effectiveRolePath}/timetable`, permission: "timetable.read" },
        { title: "Generate Timetable", icon: <AutoAwesome />, path: `${effectiveRolePath}/timetable/generate`, permission: "timetable.read" },
        { title: "Schedule Change", icon: <AccessTime />, path: `${effectiveRolePath}/schedule-change`, permission: "schedule.request_change" },
        { title: "Edit Lecture", icon: <EventNote />, path: `${effectiveRolePath}/edit-lecture/department-schedule`, permissions: EDIT_LECTURE_MENU_PERMISSIONS },
      ],
    },
    {
      title: "Guardian Faculty",
      icon: <SupervisorAccount />,
      submenu: [
        { title: "Defaulter Dashboard", icon: <Assessment />, path: `${effectiveRolePath}/gfm/defaulters`, permissions: ["gfm.mentees.view", "gfm.view_all"] },
        { title: "Guardian Faculty Assignments", icon: <SupervisorAccount />, path: `${effectiveRolePath}/gfm/assignments`, permission: "gfm.manage" },
        { title: "Guardian Faculty Settings", icon: <Settings />, path: `${effectiveRolePath}/gfm/settings`, permission: "gfm.manage" },
      ],
    },
    {
      title: "Fees Management",
      icon: <Payments />,
      submenu: [
        { title: "Fee Heads", icon: <ListAlt />, path: `${effectiveRolePath}/fees/heads`, permission: "fees.manage" },
        { title: "Fee Structure", icon: <AccountBalance />, path: `${effectiveRolePath}/fees/structures`, permission: "fees.manage" },
        { title: "Student Fees", icon: <People />, path: `${effectiveRolePath}/fees/student-fees`, permissions: ["fees.read", "fees.manage"] },
        { title: "Fee Collection", icon: <Payments />, path: `${effectiveRolePath}/fees/collection`, permission: "fees.manage" },
        { title: "Fee Reports", icon: <Assessment />, path: `${effectiveRolePath}/fees/reports`, permissions: ["fees.reports", "fees.manage"] },
        { title: "Fee Settings", icon: <Tune />, path: `${effectiveRolePath}/fees/settings`, permission: "fees.manage" },
      ],
    },
    {
      title: "Settings",
      icon: <Settings />,
      submenu: [
        { title: "Payment Setup", icon: <Settings />, path: `${effectiveRolePath}/settings/payment-setup`, permissions: ["fees.payment_setup", "fees.manage"] },
        { title: "Registration Settings", icon: <Tune />, path: `${effectiveRolePath}/registration-settings`, permissions: ["registration.prn.manage", "registration.manage"] },
        { title: "Approve Registration", icon: <ListAlt />, path: `${effectiveRolePath}/approve-registration`, permission: "registration.menu_view" },
      ],
    },
    {
      title: "Leave and Request",
      icon: <EventNote />,
      submenu: [
        {
          title: "Leave Requests",
          icon: <AccessTime />,
          path: `${effectiveRolePath}/leaves`,
          permissions: LEAVE_REVIEW_MENU_PERMISSIONS,
        },
        {
          title: "Profile Edit Requests",
          icon: <Person />,
          path: `${effectiveRolePath}/requests`,
          permissions: ["student.profile.update", "teacher.profile.update"],
        },
        {
          title: "Contact Info Requests",
          icon: <Settings />,
          path: "/profile/contact-requests",
          permissions: ["contact.request_edit"],
        },
        {
          title: "Device Login Approvals",
          icon: <Security />,
          path: `${effectiveRolePath}/requests/device-logins`,
          permissions: [
            "device.student.read",
            "device.student.approve",
            "device.student.reject",
            "device.staff.read",
            "device.staff.approve",
            "device.staff.reject",
            "student.profile.update",
            "teacher.profile.update",
          ],
        },
      ],
    },
    {
      title: "Account and Support",
      icon: <Support />,
      submenu: [
        { title: "Profile", icon: <Person />, path: "/profile", permission: "profile.read" },
        { title: "Help and Support", icon: <HelpOutline />, path: "/help-faqs", permissions: ["organization.read", "user.read", "department.read"] },
        { title: "Support messages", icon: <Support />, path: "/chat", permission: "chat.access" },
      ],
    },
    { title: "Organization", icon: <Business />, path: "/organization", permission: "organization.read" },
    { title: "Messages", icon: <Chat />, path: "/chat", permissions: CHAT_MENU_PERMISSIONS },
  ];

  const getMenusByPermission = () => {
    // Super admin in institute context: special layout (no permission filter on some items)
    if (userRole === "superadmin" && isInInstituteContext) {
      return filterByPermission(instituteContextMenus);
    }

    // All other cases: build from dynamic RBAC config
    const builtMenus = buildMenuFromConfig();

    // Custom order when logged-in role is institute/admin (same sidebar order)
    if (normalizedRole === "institute" || normalizedRole === "admin" || userRole === "admin") {
      const preferredOrder = [
        "Dashboard",
        "Administration",
        "Academics",
        "Department",
        "Attendance",
        "Schedule & Timetable",
        "Guardian Faculty",
        "Leave and Request",
        "Account and Support",
        "Notifications",
        "Messages",
        "Organization",
        "Apply Leave",
      ];

      const rank = new Map(preferredOrder.map((title, index) => [title, index]));
      return [...builtMenus].sort((a, b) => {
        const aRank = rank.has(a.title) ? rank.get(a.title) : Number.MAX_SAFE_INTEGER;
        const bRank = rank.has(b.title) ? rank.get(b.title) : Number.MAX_SAFE_INTEGER;
        if (aRank !== bRank) return aRank - bRank;
        return 0;
      });
    }

    // Custom order for HOD role
    if (normalizedRole === "hod" || userRole === "hod") {
      const preferredOrder = [
        "Dashboard",
        "Administration",
        "Department",
        "Academics",
        "Attendance",
        "Schedule & Timetable",
        "Guardian Faculty",
        "Leave and Request",
        "Account and Support",
        "Messages",
        "Apply Leave",
      ];
      const rank = new Map(preferredOrder.map((title, index) => [title, index]));
      return [...builtMenus].sort((a, b) => {
        const aRank = rank.has(a.title) ? rank.get(a.title) : Number.MAX_SAFE_INTEGER;
        const bRank = rank.has(b.title) ? rank.get(b.title) : Number.MAX_SAFE_INTEGER;
        if (aRank !== bRank) return aRank - bRank;
        return 0;
      });
    }

    // Custom order for teacher role
    if (normalizedRole === "teacher" || userRole === "teacher") {
      const preferredOrder = [
        "Dashboard",
        "Academics",
        "Department",
        "Attendance",
        "Schedule & Timetable",
        "Guardian Faculty",
        "Leave and Request",
        "Account and Support",
        "Messages",
        "Apply Leave",
      ];
      const rank = new Map(preferredOrder.map((title, index) => [title, index]));
      return [...builtMenus].sort((a, b) => {
        const aRank = rank.has(a.title) ? rank.get(a.title) : Number.MAX_SAFE_INTEGER;
        const bRank = rank.has(b.title) ? rank.get(b.title) : Number.MAX_SAFE_INTEGER;
        if (aRank !== bRank) return aRank - bRank;
        return 0;
      });
    }

    return builtMenus;
  };

  // Auto-expand parent menu when current path matches a submenu item
  React.useEffect(() => {
    const path = location.pathname;
    const menus = getMenusByPermission();
    const toExpand = menus
      .filter((m) => m.submenu?.some((s) => s.path && (path === s.path || path.startsWith(s.path + "/"))))
      .map((m) => m.title);
    if (toExpand.length > 0) {
      setOpenMenus((prev) => {
        const next = { ...prev };
        toExpand.forEach((title) => { next[title] = true; });
        return next;
      });
      setTimeout(() => ensureMenuVisible(toExpand[0]), 220);
    }
  }, [location.pathname, userRole, isInInstituteContext, ensureMenuVisible]);

  const menuKeyFor = (parentKey, title) =>
    parentKey ? `${parentKey} > ${title}` : title;

  const renderMenuItem = (item, index, parentKey = "", depth = 0) => {
    const key = menuKeyFor(parentKey, item.title);
    const padLeft = 2 + depth * 2;

    if (item.submenu) {
      const isOpen = openMenus[key];
      const submenuPaths = item.submenu.map((sub) => sub.path).filter(Boolean);
      const active = submenuPaths.length ? isMenuActive(submenuPaths) : false;

      return (
        <Box
          key={key || index}
          ref={(node) => {
            if (node) menuRefs.current[key] = node;
          }}
        >
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => handleMenuToggle(key)}
              sx={{ ...menuItemStyles(active), pl: padLeft }}
            >
              <ListItemIcon sx={{ color: "inherit", minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.title} />
              {isOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
          </ListItem>
          <Collapse in={!!isOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.submenu.map((subItem, subIndex) =>
                renderMenuItem(subItem, `${index}-${subIndex}`, key, depth + 1)
              )}
            </List>
          </Collapse>
        </Box>
      );
    }

    return (
      <ListItem
        key={key || index}
        disablePadding
        ref={(node) => {
          if (node) menuRefs.current[key] = node;
        }}
      >
        <ListItemButton
          onClick={() => handleNavigation(item.path)}
          sx={{ ...menuItemStyles(isActive(item.path)), pl: padLeft }}
        >
          <ListItemIcon sx={{ color: "inherit", minWidth: 40 }}>
            {item.icon}
          </ListItemIcon>
          <ListItemText primary={item.title} />
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <Box
      sx={{
        height: "100%",
        bgcolor: "#1a1f37",
        color: "#fff",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Logo Section */}
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h5" fontWeight={700} sx={{ color: "#fff" }}>
          {usePlatformBranding ? platformBranding.logoText : "ERP Panel"}
        </Typography>
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
          {usePlatformBranding ? "Platform Administration" : "Educational Management"}
        </Typography>
      </Box>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />

      {/* User Info - when in institute context (superadmin or admin), show institute image/initial */}
      {(() => {
        const effectiveInstitute =
          usePlatformBranding
            ? null
            : userRole === "superadmin"
              ? selectedInstitute
              : selectedInstitute || user?.institute || null;
        const showInstituteAvatar =
          !usePlatformBranding &&
          ((userRole === "superadmin" && isInInstituteContext && selectedInstitute) ||
            (userRole === "admin" && effectiveInstitute));
        return (
          <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              src={
                showInstituteAvatar && effectiveInstitute
                  ? (effectiveInstitute.profileImage || effectiveInstitute.avatar || undefined)
                  : (user?.profileImage || user?.avatar)
              }
              sx={{ width: 48, height: 48, bgcolor: "primary.main" }}
            >
              {showInstituteAvatar && effectiveInstitute && !effectiveInstitute.profileImage && !effectiveInstitute.avatar
                ? getAvatarInitial(effectiveInstitute)
                : getAvatarInitial(user)}
            </Avatar>
            <Box>
              <Typography variant="subtitle2" fontWeight={600}>
                {user?.firstName} {user?.lastName}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(255,255,255,0.6)",
                  textTransform: "capitalize",
                }}
              >
                {userRole}
              </Typography>
            </Box>
          </Box>
        );
      })()}

      <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />

      {/* Institute Context Banner - show when in institute context (superadmin or admin) */}
      {isInInstituteContext && selectedInstitute && !usePlatformBranding && (userRole === "superadmin" || userRole === "admin") && (
        <>
          <Box
            sx={{
              p: 2,
              bgcolor: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              "&:hover": { bgcolor: "primary.dark" },
            }}
            onClick={() => handleNavigation("/organization")}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, overflow: "hidden", minWidth: 0 }}>
              <Avatar
                src={selectedInstitute?.profileImage || selectedInstitute?.avatar || undefined}
                sx={{ width: 40, height: 40, bgcolor: "rgba(255,255,255,0.2)", flexShrink: 0 }}
              >
                {getAvatarInitial(selectedInstitute)}
              </Avatar>
              <Box sx={{ overflow: "hidden", minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{ opacity: 0.8, color: "#fff" }}
                >
                  Managing Institute:
                </Typography>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{
                    color: "#fff",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {selectedInstitute?.name}
                </Typography>
              </Box>
            </Box>
            <IconButton
              size="small"
              sx={{ color: "#fff", ml: 1 }}
              onClick={(e) => { e.stopPropagation(); handleExitInstitute(); }}
              title="Exit Institute"
            >
              <ExitToApp />
            </IconButton>
          </Box>
          <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />
        </>
      )}

      {/* Navigation Menu */}
      <Box ref={navScrollRef} sx={{ flexGrow: 1, overflowY: "auto", py: 1 }}>
        <List>
          {getMenusByPermission().map((item, index) => renderMenuItem(item, index))}
        </List>
      </Box>

      {/* Settings - commented for now */}
      {/* <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />
      <Box sx={{ p: 1 }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleNavigation("/settings")}
            sx={menuItemStyles(isActive("/settings"))}
          >
            <ListItemIcon sx={{ color: "inherit", minWidth: 40 }}>
              <Settings />
            </ListItemIcon>
            <ListItemText primary="Settings" />
          </ListItemButton>
        </ListItem>
      </Box> */}
    </Box>
  );
};

export default Sidebar;